/**
 * Маршрут согласования: таймлайн шагов (RouteTimeline), его предпросмотр в форме подачи
 * (RoutePreview) и выделенный блок «следующий шаг» (NextStep). Все три рисуют ОДИН формат
 * шага — расхождения «в форме одно, в карточке другое» быть не может.
 */
import { useState } from 'react'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../../i18n'
import { tone } from '../../../theme/tones'
import { Avatar, Icon } from '../../../ui'
import { pressable } from '../../../ui/primitives'
import type { EdoType } from '../../../lib/types'
import type { RouteStep } from '../registry'
import { currentStep, routeOf } from '../processVM'
import { splitRoute, windowRoute, type RouteSlot } from '../processCard'
import { CommentNote } from './CommentNote'
import { useRoutePreview } from '../useEdo'

const STATE_META: Record<RouteStep['state'], { tone: string; icon: string; labelKey: string }> = {
  done: { tone: 'green', icon: 'check', labelKey: 'proc.sDone' },
  current: { tone: 'blue', icon: 'clock', labelKey: 'proc.sCurrent' },
  pending: { tone: 'default', icon: 'circle', labelKey: 'proc.sPending' },
  rejected: { tone: 'red', icon: 'x', labelKey: 'proc.sRejected' },
  skipped: { tone: 'gold', icon: 'skip-forward', labelKey: 'proc.sSkipped' }, // эскалировано по таймеру
  // Условная ветка не сработала (нет рук./Рук.СП, грейд без ГД) либо документ закрыт раньше шага.
  na: { tone: 'default', icon: 'circle', labelKey: 'proc.sNa' },
}

/**
 * Маршрут в карточке процесса. `collapse` (дровер и /processes/[id], макет 16.10.2026): шаги, что
 * были или идут, — полностью; будущие требуемые — строкой; «не потребовались» — одной
 * раскрывающейся строкой («Ещё N шагов не потребовались» у закрытого документа, «Дальше ещё N
 * шагов» у идущего). Без `collapse` (предпросмотр в форме подачи) — все шаги полностью.
 */
export function RouteTimeline({ route, collapse }: { route: RouteStep[]; collapse?: 'terminal' | 'active' }) {
  const { l, lp } = useApp()
  const [naOpen, setNaOpen] = useState(false)
  if (!collapse) {
    return (
      <YStack>
        {route.map((r, i) => (
          <FullStep key={i} r={r} last={i === route.length - 1} />
        ))}
      </YStack>
    )
  }
  const { shown, na } = splitRoute(route)
  return (
    <YStack>
      {shown.map(({ step, compact }, i) =>
        compact ? (
          <StepLine key={i} r={step} />
        ) : (
          <FullStep key={i} r={step} last={i === shown.length - 1 || shown[i + 1].compact} />
        ),
      )}
      {na.length ? (
        <>
          <XStack
            {...pressable}
            onPress={() => setNaOpen(!naOpen)}
            aria-expanded={naOpen}
            cursor="pointer"
            alignSelf="flex-start"
            alignItems="center"
            gap={6}
            minHeight={44}
            marginTop={4}
            hoverStyle={{ opacity: 0.8 }}
          >
            <Stack rotate={naOpen ? '90deg' : '0deg'} animation="quick">
              <Icon name="chevron-right" size={16} color="$muted" />
            </Stack>
            <Text fontSize={13} fontWeight="600" color="$muted">
              {naOpen ? l('card.hide') : lp(collapse === 'terminal' ? 'card.naDone' : 'card.naActive', na.length)}
            </Text>
          </XStack>
          {naOpen ? na.map((r, i) => <StepLine key={i} r={r} />) : null}
        </>
      ) : null}
    </YStack>
  )
}

/**
 * Маршрут в одну линию — для главной («Требуют внимания», когда документов один-два: заполняет ряд,
 * владелец 02.11.2026). Тот же формат шага и цвета состояний, что у RouteTimeline, только шаги идут
 * слева направо; «не требуется» не рисуем. `max` — сколько мест влезает в ширину: длинный маршрут
 * (десяток согласующих) сворачивается вокруг текущего шага, пропуски — узлом «+N» (windowRoute).
 * Отрезок к месту цветной, если документ до него дошёл.
 */
export function RouteStepper({ route, max = Infinity }: { route: RouteStep[]; max?: number }) {
  const { t, l, lp } = useApp()
  const slots = windowRoute(route, max)
  if (!slots.length) return null
  const first = (s: RouteSlot) => (s.kind === 'step' ? s.step : s.steps[0])
  const reached = (i: number) => i >= 0 && i < slots.length && first(slots[i]).state !== 'pending'
  const line = (on: boolean) => (on ? tone('green').solid : '$borderSecondary')
  return (
    <XStack>
      {slots.map((s, i) => {
        const r = first(s)
        const gap = s.kind === 'gap' ? s.steps : null
        const sm = STATE_META[r.state]
        const tn = tone(sm.tone)
        const dim = r.state === 'pending'
        let title: string, who: string, note: string, current = false
        if (gap) {
          // промежуток: «Ещё 4 шага», под ним — общий итог, если он один у всех («Согласовано», «Ожидает»)
          const same = gap.every((g) => g.state === gap[0].state)
          title = lp('route.more', gap.length)
          who = ''
          note = same ? l(STATE_META[gap[0].state].labelKey) : ''
        } else {
          title = t(r.act)
          who = r.person?.name ?? (r.group ? t(r.group) : '')
          // пройденный шаг — когда решили («2 дня назад»), остальные — состояние («В работе», «Ожидает»)
          note = r.state === 'done' && r.when ? t(r.when) : l(sm.labelKey)
          current = r.state === 'current'
        }
        const gapDone = !!gap && gap.every((g) => g.state === 'done')
        return (
          <YStack key={i} flex={1} flexBasis={0} minWidth={0} alignItems="center" gap={7}>
            <XStack alignSelf="stretch" alignItems="center">
              <Stack flex={1} height={2} backgroundColor={i === 0 ? 'transparent' : line(reached(i))} />
              {gap ? (
                <Stack
                  height={24}
                  minWidth={32}
                  paddingHorizontal={7}
                  borderRadius={999}
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  borderWidth={1}
                  borderColor={gapDone ? tone('green').solid : '$borderColor'}
                  backgroundColor={gapDone ? tone('green').soft : '$background'}
                >
                  <Text fontSize={11} fontWeight="700" color={gapDone ? tone('green').text : '$muted'}>
                    +{gap.length}
                  </Text>
                </Stack>
              ) : (
                <Stack
                  width={24}
                  height={24}
                  borderRadius={999}
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  backgroundColor={dim ? '$borderSecondary' : tn.solid}
                >
                  <Icon name={sm.icon} size={12} color={dim ? '$tertiary' : '#fff'} />
                </Stack>
              )}
              <Stack flex={1} height={2} backgroundColor={i === slots.length - 1 ? 'transparent' : line(reached(i + 1))} />
            </XStack>
            <YStack alignItems="center" gap={2} paddingHorizontal={6} maxWidth="100%">
              <Text fontSize={12} fontWeight="600" color={dim || gap ? '$muted' : '$color'} numberOfLines={2} textAlign="center">
                {title}
              </Text>
              {who ? (
                <Text fontSize={11} color="$muted" numberOfLines={1} textAlign="center">
                  {who}
                </Text>
              ) : null}
              {note ? (
                <Text fontSize={10} fontWeight={current ? '700' : '400'} color={current ? tn.text : '$tertiary'} numberOfLines={1}>
                  {note}
                </Text>
              ) : null}
            </YStack>
          </YStack>
        )
      })}
    </XStack>
  )
}

/** Будущий или «не потребовавшийся» шаг одной строкой: название и кто (персона или группа). */
function StepLine({ r }: { r: RouteStep }) {
  const { t } = useApp()
  const who = r.person?.name ?? (r.group ? t(r.group) : '')
  return (
    <XStack alignItems="center" justifyContent="space-between" gap={10} paddingVertical={6} paddingLeft={41}>
      <Text fontSize={13} color="$muted" flexShrink={1}>
        {t(r.act)}
      </Text>
      {who ? (
        <Text fontSize={12} color="$tertiary" numberOfLines={1} flexShrink={1} textAlign="right">
          {who}
        </Text>
      ) : null}
    </XStack>
  )
}

/** Шаг полностью: кружок состояния, название, статус, исполнитель с должностью, комментарий. */
function FullStep({ r, last }: { r: RouteStep; last: boolean }) {
  const { t, l } = useApp()
  // Ответственный шага: только живые данные (r.person).
  const personName = r.person?.name
  const personSeed = r.person?.id
  const sm = STATE_META[r.state]
  const tn = tone(sm.tone)
  // Приглушаем то, чего ещё не было («ожидает») и чего не будет («не требуется»).
  const dim = r.state === 'pending' || r.state === 'na'
  return (
    <XStack gap={13}>
      <YStack alignItems="center" flexShrink={0}>
        <Stack
          width={28}
          height={28}
          borderRadius={999}
          alignItems="center"
          justifyContent="center"
          backgroundColor={dim ? '$borderSecondary' : tn.solid}
        >
          <Icon name={sm.icon} size={14} color={dim ? '$tertiary' : '#fff'} />
        </Stack>
        {!last && <Stack width={2} flex={1} minHeight={26} backgroundColor="$borderSecondary" marginVertical={2} />}
      </YStack>
      <YStack flex={1} minWidth={0} paddingBottom={last ? 0 : 16}>
        <XStack alignItems="center" gap={8} flexWrap="wrap">
          <Text fontSize={13} fontWeight="600" color={dim ? '$muted' : '$color'}>
            {t(r.act)}
          </Text>
          <XStack backgroundColor={tn.soft} borderRadius={999} paddingHorizontal={7} paddingVertical={1}>
            <Text fontSize={10} fontWeight="700" color={tn.text}>
              {l(sm.labelKey)}
            </Text>
          </XStack>
        </XStack>
        {personName ? (
          <XStack alignItems="flex-start" gap={7} marginTop={6}>
            <Avatar name={personName} seed={personSeed} size="xs" />
            <YStack flex={1} minWidth={0}>
              <XStack alignItems="center" gap={7} flexWrap="wrap">
                <Text fontSize={11} color="$muted">
                  {personName}
                </Text>
                {r.when && (
                  <>
                    <Text color="$borderColor">·</Text>
                    <Text fontSize={11} color="$muted">
                      {t(r.when)}
                    </Text>
                  </>
                )}
              </XStack>
              {/* Должность согласующего — из карточки сотрудника (User.jobTitle). */}
              {r.person?.position ? (
                <Text fontSize={10} color="$tertiary" numberOfLines={1}>
                  {r.person.position}
                </Text>
              ) : null}
            </YStack>
          </XStack>
        ) : r.group ? (
          <YStack marginTop={6} gap={2}>
            <XStack alignItems="center" gap={7}>
              <Icon name="users" size={12} color="$tertiary" />
              <Text fontSize={11} color="$muted">
                {t(r.group)}
              </Text>
              {r.when && (
                <>
                  <Text color="$borderColor">·</Text>
                  <Text fontSize={11} color="$muted">
                    {t(r.when)}
                  </Text>
                </>
              )}
            </XStack>
            {/* Кандидаты группового шага (только предпросмотр): персона будет выбрана
                claim/assign, но кто МОЖЕТ взять шаг — видно уже сейчас. */}
            {r.candidates?.length ? (
              <Text fontSize={10} color="$tertiary" marginLeft={19}>
                {r.candidates.map((c) => c.name).join(', ')}
              </Text>
            ) : null}
          </YStack>
        ) : null}
        {r.comment ? <CommentNote text={r.comment} /> : null}
      </YStack>
    </XStack>
  )
}

/**
 * Маршрут БУДУЩЕЙ заявки в форме подачи — «кто её согласует» до нажатия «Отправить».
 * Рисует тот же таймлайн, что и карточка после подачи (RouteTimeline по route[] с сервера),
 * поэтому расхождения «в форме одно, в карточке другое» быть не может.
 *
 * Пустой маршрут (произвольный документ — там маршрут собирает сам инициатор) и ошибку запроса
 * показываем одинаково: блока просто нет. Он справочный и подачу заявки блокировать не должен.
 */
export function RoutePreview({
  type,
  grade,
  onBehalfOfUserId,
  employmentId,
  enabled = true,
}: {
  type: EdoType
  grade?: string
  /** Подача «от имени» (роль proxy): маршрут строится от указанного сотрудника. */
  onBehalfOfUserId?: string
  employmentId?: string
  enabled?: boolean
}) {
  const { t } = useApp()
  const { data } = useRoutePreview(type, grade, onBehalfOfUserId, employmentId, enabled)
  if (!data?.length) return null
  return (
    <YStack gap={12} borderTopWidth={1} borderColor="$borderSecondary" paddingTop={14}>
      <Text fontSize={13} fontWeight="700" color="$color">
        {t({ ru: 'Маршрут согласования', en: 'Approval route' })}
      </Text>
      <RouteTimeline route={routeOf(undefined, data)} />
      <Text fontSize={10} color="$tertiary">
        {t({
          ru: 'Предварительный маршрут: изменится руководитель или должность — изменится и он.',
          en: 'Preview: the route changes if a manager or a position changes.',
        })}
      </Text>
    </YStack>
  )
}

/**
 * «Следующий шаг» — компактный выделенный блок над маршрутом: активный этап (currentStep),
 * его статус (существующие лейблы STATE_META) и ответственный. Derive из уже построенного route
 * без новых запросов; нет активного шага (документ завершён) → блок скрыт целиком.
 */
export function NextStep({ route }: { route: RouteStep[] }) {
  const { t, l } = useApp()
  const r = currentStep({ route })
  if (!r) return null
  const sm = STATE_META[r.state]
  const tn = tone(sm.tone)
  const personName = r.person?.name
  return (
    <YStack gap={6} marginBottom={12} borderRadius={7} borderLeftWidth={3} borderColor={tn.solid} backgroundColor={tn.soft} paddingHorizontal={13} paddingVertical={11}>
      <XStack alignItems="center" gap={8} flexWrap="wrap">
        <Text fontSize={10} color="$muted" textTransform="uppercase" letterSpacing={0.4}>
          {l('proc.nextStep')}
        </Text>
        <XStack backgroundColor="$background" borderRadius={999} paddingHorizontal={7} paddingVertical={1}>
          <Text fontSize={10} fontWeight="700" color={tn.text}>
            {l(sm.labelKey)}
          </Text>
        </XStack>
      </XStack>
      <Text fontSize={13} fontWeight="700" color="$color">
        {t(r.act)}
      </Text>
      {personName ? (
        <XStack alignItems="center" gap={7} flexWrap="wrap">
          <Avatar name={personName} seed={r.person?.id} size="xs" />
          <Text fontSize={11} color="$muted">
            {personName}
          </Text>
          {r.person?.position ? (
            <>
              <Text color="$borderColor">·</Text>
              <Text fontSize={11} color="$tertiary">
                {r.person.position}
              </Text>
            </>
          ) : null}
        </XStack>
      ) : r.group ? (
        <XStack alignItems="center" gap={7}>
          <Icon name="users" size={12} color="$tertiary" />
          <Text fontSize={11} color="$muted">
            {t(r.group)}
          </Text>
        </XStack>
      ) : null}
    </YStack>
  )
}
